import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Warning } from '@mui/icons-material';
import { useTrainingManagement } from '../hooks/useTrainingManagement';
import { TrainingCourse } from '../services/trainingManagementService';

interface CourseDeleteModalProps {
  open: boolean;
  course: TrainingCourse;
  onClose: () => void;
  onSuccess: () => void;
}

export default function CourseDeleteModal({
  open,
  course,
  onClose,
  onSuccess,
}: CourseDeleteModalProps) {
  const { deleteCourse } = useTrainingManagement();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await deleteCourse(course.id);
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to delete course');
    } finally {
      setDeleting(false);
    }
  };

  const hasEnrollments = (course.enrollmentCount || 0) > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Warning color="error" />
          <Typography variant="h6">Delete Course</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Are you sure you want to delete this course?
        </Typography>

        <Box bgcolor="grey.100" p={2} borderRadius={1} my={2}>
          <Typography variant="body2" fontWeight="bold">
            {course.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {course.description}
          </Typography>
        </Box>

        {hasEnrollments ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              This course has <strong>{course.enrollmentCount} active enrollments</strong>.
              It will be archived instead of permanently deleted to preserve learner data.
            </Typography>
          </Alert>
        ) : (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              This action cannot be undone. The course and all associated data will be
              permanently deleted.
            </Typography>
          </Alert>
        )}

        <Box>
          <Typography variant="body2" color="text.secondary">
            • Enrollments: {course.enrollmentCount || 0}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Certificates issued: {course.certificateCount || 0}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Status: {course.status}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={deleting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={deleting}
          startIcon={deleting && <CircularProgress size={16} />}
        >
          {deleting ? 'Deleting...' : hasEnrollments ? 'Archive Course' : 'Delete Course'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

